import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { DevToolSection, devResultPanel, devMono } from '/imports/ui/DevToolSection';
import { analyzeRegistry, type RegistryReport } from '/imports/api/devtools';

export const DevToolRegistry = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<RegistryReport | null>(null);

  const run = async () => {
    setLoading(true);
    try {
      setReport(await analyzeRegistry.call({ maxAgeDays: 30 }));
    }
    catch (e: any) {
      toast.error(e?.reason || e?.message || `${e}`);
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <DevToolSection
      title="Registry Opportunity Analysis"
      description="Read-only scan of the <code>chainQuotes</code> registry: does the bot-to-bot channel have money in it? Reports cards a chain bids above our exit value (stocking targets), cross-chain arbitrage spreads, and how far the rest fall short."
      buttonText="Analyze registry"
      onRun={run}
      loading={loading}
    >
      {report && (
        <div className={devResultPanel}>
          <div className={devMono}>
            {report.quotesConsidered} quotes · {report.distinctCards} cards · {report.distinctChains} chains
          </div>
          <div className="mt-3">
            <strong>Sell-above-cost ({report.sellOpportunities.length}):</strong> a chain bids more than our exit.
            <table className="mt-1 w-full text-[12px]">
              <thead className="text-fg-3"><tr><th className="text-left">Card</th><th className="text-right">Their bid</th><th className="text-right">Our exit</th><th className="text-right">Margin</th></tr></thead>
              <tbody className={devMono}>
                {report.sellOpportunities.slice(0, 25).map((o) => (
                  <tr key={`${o.cid}-${o.uid}`}>
                    <td>{o.name}</td>
                    <td className="text-right">{o.theirBid.toFixed(3)}</td>
                    <td className="text-right">{o.ourExit.toFixed(3)}</td>
                    <td className="text-right text-green">+{o.margin.toFixed(3)}</td>
                  </tr>
                ))}
                {!report.sellOpportunities.length && <tr><td className="text-fg-3">none &mdash; no chain overpays vs our exit</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <strong>Arbitrage ({report.arbitrage.length}):</strong> one chain&rsquo;s ask under another&rsquo;s bid.
            <table className="mt-1 w-full text-[12px]">
              <thead className="text-fg-3"><tr><th className="text-left">Card</th><th className="text-right">Buy @</th><th className="text-right">Sell @</th><th className="text-right">Spread</th></tr></thead>
              <tbody className={devMono}>
                {report.arbitrage.slice(0, 25).map((a) => (
                  <tr key={a.cid}>
                    <td>{a.name}</td>
                    <td className="text-right">{a.ask.toFixed(3)}</td>
                    <td className="text-right">{a.bid.toFixed(3)}</td>
                    <td className="text-right text-green">+{a.spread.toFixed(3)}</td>
                  </tr>
                ))}
                {!report.arbitrage.length && <tr><td className="text-fg-3">none &mdash; no cross-chain spread</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <strong>Live shopping list ({report.shoppingList.length}):</strong> cards inventory is currently chasing.
            <table className="mt-1 w-full text-[12px]">
              <thead className="text-fg-3"><tr><th className="text-left">Card</th><th className="text-right">Their bid</th><th className="text-right">Our exit</th><th className="text-right">Margin</th></tr></thead>
              <tbody className={devMono}>
                {report.shoppingList.slice(0, 25).map((o) => (
                  <tr key={o.cid}>
                    <td>{o.name}</td>
                    <td className="text-right">{o.theirBid.toFixed(3)}</td>
                    <td className="text-right">{o.ourExit.toFixed(3)}</td>
                    <td className="text-right text-green">+{o.margin.toFixed(3)}</td>
                  </tr>
                ))}
                {!report.shoppingList.length && <tr><td className="text-fg-3">empty &mdash; the recompute job has not run, or no card clears the margin cutoff</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3">
            <strong>Shortfall of the rest:</strong>{' '}
            <span className={devMono}>{report.missBuckets.map((b) => `${b.label}: ${b.count}`).join('  ·  ')}</span>
          </div>
        </div>
      )}
    </DevToolSection>
  );
};
